import { useMutation } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AwarenessLogo } from '@/components/AwarenessLogo';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useAuth } from '@/hooks/useAuth';
import { useColors } from '@/hooks/useColors';
import { useRedirectPath } from '@/hooks/useRedirectPath';
import { useToaster } from '@/hooks/useToaster';

export default function VerificationCodeExpired() {
  const { email } = useLocalSearchParams<{
    email: string;
  }>();

  const { forgotPassword } = useAuth();

  const { replace } = useRouter();

  const { colors } = useColors();

  const toast = useToaster();

  const { appendRedirectPath } = useRedirectPath();

  const { mutate, isPending } = useMutation({
    mutationFn: () => forgotPassword(email),
    onSuccess: () => {
      replace(appendRedirectPath(`/forgot-password/success?email=${encodeURIComponent(email)}`));
    },
    onError: (error) => {
      console.error('CreatePassword/VerificationCodeExpired forgotPassword ERROR:', error);

      toast.showError({
        title: 'There was an issue with your submission. Please try again.',
      });
    },
  });

  const { top, bottom } = useSafeAreaInsets();

  return (
    <VStack
      className="flex-1"
      style={{
        paddingTop: top,
        paddingBottom: bottom,
      }}>
      <VStack className="flex-1 items-center justify-center px-5">
        <VStack className="pb-16">
          <AwarenessLogo alt="Primum mark" kind="mark" box={{ height: 140, width: 255 }} />
        </VStack>

        <Heading size="h1-xl" className="mb-2">
          Password link expired
        </Heading>

        <Text className="mb-8 text-center">
          The reset password link has expired. We’re unable to create a new password. Please request
          a new reset password link below.
        </Text>

        <Button onPress={() => mutate()} disabled={isPending} className="app:w-full web:w-fit">
          {isPending ? <ButtonSpinner color={colors.primary[200]} /> : null}

          <ButtonText>Resend forgot password link</ButtonText>
        </Button>
      </VStack>
    </VStack>
  );
}
